import { Navigate, Route, Routes, BrowserRouter } from 'react-router';
import ActiveLoanGuard from '@/Router/Guards/ActiveLoanGuard';
import RepeatedLoanGuard from '@/Router/Guards/RepeatedLoanGuard';
import WithdrawGuard from '@/Router/Guards/WithdrawGuard';
import { AsyncWithdrawContractConsents } from '@/pages/WithdrawContractFlow/Consents/async';
import { AsyncWithdrawContractDashboardBroken } from '@/pages/WithdrawContractFlow/DashboardBrokenWithdraw/async';
import { AsyncWithdrawContractDashboard } from '@/pages/WithdrawContractFlow/DashboardWithdraw/async';
import { AsyncWithdrawContractIntroConfirm } from '@/pages/WithdrawContractFlow/IntroConfirm/async';
import { AsyncWithdrawContractWarning } from '@/pages/WithdrawContractFlow/Warning/async';
import { AsyncAdjustIncome } from '@/pages/AdjustIncome/async';
import { AsyncExpiredIncome } from '@/pages/ExpiredIncome/async';
import { AsyncOverdueDebtSchedule } from '@/pages/OverdueDebtSchedule/async';
import { AsyncNotFound } from '@/pages/NotFound/async';
import { AsyncPendingLoan } from '@/pages/PendingLoan/async';
import * as R from '@/Router/routes';
import { AsyncActiveLoan } from '@/pages/ActiveLoan/async';
import { AsyncAdditionalAmount } from '@/pages/AdditionalAmount/async';
import {
  AsyncDocumentUploadPending,
  AsyncKYCUploadPending,
} from '@/pages/DocumentUploadPending/async';
import { AsyncDocumentUploadLater } from '@/pages/DocumentUploadLater/async';
import { AsyncLoanApproved } from '@/pages/LoanApproved/async';
import { AsyncLoanBudget } from '@/pages/LoanBudget/async';
import { AsyncLoanConfirm } from '@/pages/LoanConfirm/async';
import { AsyncLoanRejected } from '@/pages/LoanRejected/async';
import { AsyncNewOfferDecreased } from '@/pages/NewOfferDecreased/async';
import {
  AsyncNewOfferIncreased,
  AsyncNewOfferIncreasedCalculator,
} from '@/pages/NewOfferIncreased/async';
import { AsyncOverdueLoan } from '@/pages/OverdueLoan/async';
import { AsyncBrokenAgreement } from '@/pages/BrokenAgreement/async';
import { AsyncPasswordRecovery } from '@/pages/PasswordRecovery/async';
import { AsyncPasswordRecoveryNewPassword } from '@/pages/PasswordRecoveryNewPassword/async';
import { AsyncPasswordRecoveryNewPasswordSet } from '@/pages/PasswordRecoveryNewPasswordSet/async';
import { AsyncPasswordRecoverySent } from '@/pages/PasswordRecoverySent/async';
import { AsyncPersonalDataConfirm } from '@/pages/PersonalDataConfirm/async';
import { AsyncProfile } from '@/pages/Profile/async';
import { AsyncPSD2AccountConfirm } from '@/pages/PSD2AccountConfirm/async';
import { AsyncPSD2AccountInstructions } from '@/pages/PSD2AccountInstructions/async';
import { AsyncPSD2Approved } from '@/pages/PSD2Approved/async';
import { AsyncPSD2BankAccountSelect } from '@/pages/PSD2SelectBankAccount/async';
import { AsyncPSD2Banks } from '@/pages/PSD2Banks/async';
import { AsyncPSD2Consents } from '@/pages/PSD2Consents/async';
import { AsyncPSD2Error } from '@/pages/PSD2Error/async';
import { AsyncPSD2Receiving } from '@/pages/PSD2Receiving/async';
import { AsyncPSD2Verified } from '@/pages/PSD2Verified/async';
import { AsyncRepeatedLoan } from '@/pages/RepeatedLoan/async';
import { AsyncUploadDocument } from '@/pages/UploadDocument/async';
import { AsyncUploadKYC } from '@/pages/UploadKYC/async';
import { AsyncPromo } from '@/pages/Promo/async';
import NewOfferIncreasedGuard from '@/Router/Guards/NewOfferIncreasedGuard';
import PrivateRoute from '@/Router/PrivateRoute';
import PublicRoute from '@/Router/PublicRoute';
import TwoFA from '@/pages/TwoFA/TwoFA';
import { AsyncTopUpAmount } from '@/pages/TopUpAmount/async';
import { AsyncAdditionalAmountApproved } from '@/pages/AdditionalAmountApproved/async';
import { AsyncRepayment } from '@/pages/Repayment/async';

import { useGoogleTagManager } from '@/hooks/useGoogleTagManager';
import WaitingPage from '@/pages/WaitingPage/WaitingPage';
import DataLayer from '@/utils/dataLayer/DataLayer';
import Layout from '@/components/Layout/Layout';
import PageTracker from '@/hooks/context/EventCollector/usePageTracker';
import KeycloakLoginConfirm from '@/pages/Login/KeycloakLoginConfirm';
import AsyncFAQ from '@/pages/FAQ/async';
import AsyncLoanExamples from '@/pages/LoanExamples/async';
import { AsyncPhoneCode } from '@/pages/PhoneCode/async';
import { LoginRoute } from '@/Router/LoginRoute.tsx';
import { AsyncOneTimeTokenLegacyLogin } from '@/pages/Login/LegacyLogin/async.tsx';

export default function AppRouter() {
  useGoogleTagManager();

  return (
    <div>
      <BrowserRouter>
        <DataLayer />
        <PageTracker />
        {/* <VccLiveChat accountId={VCC_LIVE_CHAT.ACCOUNT_ID} checkDebtorStatus /> */}
        <Layout>
          <Routes>
            <Route path="/" element={<Navigate to={R.LOGIN} />} />
            <Route
              path={R.LOGIN}
              element={
                <PublicRoute>
                  <LoginRoute />
                </PublicRoute>
              }
            />
            <Route
              path={R.ONE_TIME_TOKEN_LEGACY_LOGIN}
              element={
                <PublicRoute>
                  <AsyncOneTimeTokenLegacyLogin />
                </PublicRoute>
              }
            />
            <Route
              path={R.LOGIN_CONFIRM}
              element={
                <PublicRoute>
                  <KeycloakLoginConfirm />
                </PublicRoute>
              }
            />
            <Route path={R.TWO_FA} element={<TwoFA />} />
            <Route
              path={R.PASSWORD_RECOVERY}
              element={
                <PublicRoute>
                  <AsyncPasswordRecovery />
                </PublicRoute>
              }
            />
            <Route
              path={R.PASSWORD_RECOVERY_SENT}
              element={
                <PublicRoute>
                  <AsyncPasswordRecoverySent />
                </PublicRoute>
              }
            />
            <Route
              path={R.PASSWORD_RECOVERY_NEW_PASSWORD}
              element={
                <PrivateRoute>
                  <AsyncPasswordRecoveryNewPassword />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PASSWORD_RECOVERY_NEW_PASSWORD_SET}
              element={
                <PrivateRoute>
                  <AsyncPasswordRecoveryNewPasswordSet />
                </PrivateRoute>
              }
            />
            <Route
              path={R.ACTIVE_LOAN}
              element={
                <PrivateRoute>
                  <ActiveLoanGuard>
                    <AsyncActiveLoan />
                  </ActiveLoanGuard>
                </PrivateRoute>
              }
            />
            <Route
              path={R.OVERDUE_LOAN}
              element={
                <PrivateRoute>
                  <AsyncOverdueLoan />
                </PrivateRoute>
              }
            />
            <Route
              path={R.BROKEN_AGREEMENT}
              element={
                <PrivateRoute>
                  <AsyncBrokenAgreement />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PENDING_LOAN}
              element={
                <PrivateRoute>
                  <AsyncPendingLoan />
                </PrivateRoute>
              }
            />
            <Route
              path={R.WAITING_PAGE}
              element={
                <PrivateRoute>
                  <WaitingPage />
                </PrivateRoute>
              }
            />
            <Route
              path={R.OVERDUE_DEBT_SCHEDULE}
              element={
                <PrivateRoute>
                  <AsyncOverdueDebtSchedule />
                </PrivateRoute>
              }
            />
            <Route
              path={R.REPEATED_LOAN}
              element={
                <PrivateRoute>
                  <RepeatedLoanGuard>
                    <AsyncRepeatedLoan />
                  </RepeatedLoanGuard>
                </PrivateRoute>
              }
            />
            <Route
              path={R.PERSONAL_DATA_CONFIRM}
              element={
                <PrivateRoute>
                  <AsyncPersonalDataConfirm />
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADJUST_INCOME}
              element={
                <PrivateRoute>
                  <AsyncAdjustIncome />
                </PrivateRoute>
              }
            />
            <Route
              path={R.DOCUMENT_UPLOAD}
              element={
                <PrivateRoute>
                  <AsyncUploadDocument />
                </PrivateRoute>
              }
            />
            <Route
              path={R.LOAN_APPROVED}
              element={
                <PrivateRoute>
                  <AsyncLoanApproved />
                </PrivateRoute>
              }
            />
            <Route
              path={R.DOCUMENT_UPLOAD_PENDING}
              element={
                <PrivateRoute>
                  <AsyncDocumentUploadPending />
                </PrivateRoute>
              }
            />
            <Route
              path={R.DOCUMENT_UPLOAD_LATER}
              element={
                <PrivateRoute>
                  <AsyncDocumentUploadLater />
                </PrivateRoute>
              }
            />
            {[R.KYC_UPLOAD, R.KYC_ADDITIONAL_UPLOAD].map((route) => (
              <Route
                path={route}
                element={
                  <PrivateRoute>
                    <AsyncUploadKYC />
                  </PrivateRoute>
                }
              />
            ))}
            <Route
              path={R.KYC_UPLOAD_PENDING}
              element={
                <PrivateRoute>
                  <AsyncKYCUploadPending />
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADDITIONAL_AMOUNT_LOAN_BUDGET}
              element={
                <PrivateRoute>
                  <AsyncLoanBudget />
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADDITIONAL_AMOUNT_REJECT}
              element={
                <PrivateRoute>
                  <AsyncLoanRejected />
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADDITIONAL_AMOUNT_NEW_OFFER_INCREASE}
              element={
                <PrivateRoute>
                  <NewOfferIncreasedGuard additionalAmount>
                    <AsyncNewOfferIncreased additionalAmount />
                  </NewOfferIncreasedGuard>
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADDITIONAL_AMOUNT_NEW_OFFER_INCREASE_CALCULATOR}
              element={
                <PrivateRoute>
                  <NewOfferIncreasedGuard additionalAmount>
                    <AsyncAdditionalAmount />
                  </NewOfferIncreasedGuard>
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADDITIONAL_AMOUNT_APPROVED}
              element={
                <PrivateRoute>
                  <AsyncAdditionalAmountApproved />
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADDITIONAL_AMOUNT_CONFIRM}
              element={
                <PrivateRoute>
                  <AsyncLoanConfirm />
                </PrivateRoute>
              }
            />
            <Route
              path={R.LOAN_BUDGET}
              element={
                <PrivateRoute>
                  <AsyncLoanBudget />
                </PrivateRoute>
              }
            />
            <Route
              path={R.EXPIRED_INCOME}
              element={
                <PrivateRoute>
                  <AsyncExpiredIncome />
                </PrivateRoute>
              }
            />
            <Route
              path={R.LOAN_CONFIRM}
              element={
                <PrivateRoute>
                  <AsyncLoanConfirm />
                </PrivateRoute>
              }
            />
            <Route
              path={R.LOAN_REJECTED}
              element={
                <PrivateRoute>
                  <AsyncLoanRejected />
                </PrivateRoute>
              }
            />
            <Route
              path={R.NEW_OFFER_INCREASED}
              element={
                <PrivateRoute>
                  <NewOfferIncreasedGuard>
                    <AsyncNewOfferIncreased />
                  </NewOfferIncreasedGuard>
                </PrivateRoute>
              }
            />
            <Route
              path={R.NEW_OFFER_INCREASED_CALCULATOR}
              element={
                <PrivateRoute>
                  <AsyncNewOfferIncreasedCalculator />
                </PrivateRoute>
              }
            />
            <Route
              path={R.NEW_OFFER_DECREASED}
              element={
                <PrivateRoute>
                  <AsyncNewOfferDecreased />
                </PrivateRoute>
              }
            />
            <Route
              path={R.ADDITIONAL_AMOUNT}
              element={
                <PrivateRoute>
                  <AsyncAdditionalAmount />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PROFILE}
              element={
                <PrivateRoute>
                  <AsyncProfile />
                </PrivateRoute>
              }
            />
            <Route
              path={`${R.PROFILE}/:modalId`}
              element={
                <PrivateRoute>
                  <AsyncProfile />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_ACCOUNT_CONFIRM}
              element={
                <PrivateRoute>
                  <AsyncPSD2AccountConfirm />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_ACCOUNT_CHANGE_INSTRUCTIONS}
              element={
                <PrivateRoute>
                  <AsyncPSD2AccountInstructions />
                </PrivateRoute>
              }
            />

            <Route
              path={R.PSD2_BANKS}
              element={
                <PrivateRoute>
                  <AsyncPSD2Banks />
                </PrivateRoute>
              }
            />
            <Route
              path={`${R.PSD2_BANKS}/:modalId`}
              element={
                <PrivateRoute>
                  <AsyncPSD2Banks />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_BANK_ACCOUNT_SELECT}
              element={
                <PrivateRoute>
                  <AsyncPSD2BankAccountSelect />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_CONSENTS}
              element={
                <PrivateRoute>
                  <AsyncPSD2Consents />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_RECEIVING}
              element={
                <PrivateRoute>
                  <AsyncPSD2Receiving />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_VERIFIED}
              element={
                <PrivateRoute>
                  <AsyncPSD2Verified />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_ERROR}
              element={
                <PrivateRoute>
                  <AsyncPSD2Error />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PSD2_APPROVED}
              element={
                <PrivateRoute>
                  <AsyncPSD2Approved />
                </PrivateRoute>
              }
            />
            <Route
              path={R.TOP_UP_AMOUNT}
              element={
                <PrivateRoute>
                  <AsyncTopUpAmount />
                </PrivateRoute>
              }
            />
            <Route
              path={R.REPAYMENT}
              element={
                <PrivateRoute>
                  <AsyncRepayment typeOfPayment="full" />
                </PrivateRoute>
              }
            />
            <Route
              path={R.REPAYMENT_MINIMUM}
              element={
                <PrivateRoute>
                  <AsyncRepayment typeOfPayment="minimum" />
                </PrivateRoute>
              }
            />
            <Route
              path={R.REPAYMENT_CUSTOM}
              element={
                <PrivateRoute>
                  <AsyncRepayment typeOfPayment="custom" />
                </PrivateRoute>
              }
            />
            <Route
              path={R.PROMO}
              element={
                <PrivateRoute>
                  <AsyncPromo />
                </PrivateRoute>
              }
            />

            <Route
              path={R.FAQ}
              element={
                <PrivateRoute>
                  <AsyncFAQ />
                </PrivateRoute>
              }
            />
            <Route
              path={R.LOAN_EXAMPLES}
              element={
                <PrivateRoute>
                  <AsyncLoanExamples />
                </PrivateRoute>
              }
            />
            {[R.PHONE_CODE_ADDITIONAL, R.PHONE_CODE_REPEATED].map((route) => (
              <Route
                path={route}
                element={
                  <PrivateRoute>
                    <AsyncPhoneCode />
                  </PrivateRoute>
                }
              />
            ))}
            {/* WITHDRAW ROUTES */}
            <Route
              path={R.WITHDRAW_INTRO_CONFIRM}
              element={
                <PrivateRoute>
                  <AsyncWithdrawContractIntroConfirm />
                </PrivateRoute>
              }
            />
            <Route
              path={R.WITHDRAW_WARNING}
              element={
                <PrivateRoute>
                  <AsyncWithdrawContractWarning />
                </PrivateRoute>
              }
            />
            <Route
              path={R.WITHDRAW_CONSENTS}
              element={
                <PrivateRoute>
                  <AsyncWithdrawContractConsents />
                </PrivateRoute>
              }
            />
            <Route
              path={R.WITHDRAW_DASHBOARD}
              element={
                <PrivateRoute>
                  <WithdrawGuard>
                    <AsyncWithdrawContractDashboard />
                  </WithdrawGuard>
                </PrivateRoute>
              }
            />
            <Route
              path={R.WITHDRAW_DASHBOARD_BROKEN}
              element={
                <PrivateRoute>
                  <WithdrawGuard>
                    <AsyncWithdrawContractDashboardBroken />
                  </WithdrawGuard>
                </PrivateRoute>
              }
            />

            <Route path={R.NOT_FOUND_GENERAL} element={<AsyncNotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </div>
  );
}
